import React, { useId, useState } from "react";
import { Link } from "react-router-dom";
import { Sentence, Composer } from "../_shims";
import { tintClasses } from "../../shared/tints";
import { affordancesFor } from "./kindActions";
import { kindLabel, rowHeadline, canAssign } from "./kindMeta";
import MoveWorkMenu from "../work/overlays/MoveWorkMenu";
import NowAssignControl, { NowTakeButton } from "./NowAssignControl";
import { BTN_PRIMARY, BTN_QUIET } from "./buttonStyles";

// Severity is the row's urgency signal (00_CONTRACT §7 — severity contrast IS the
// hierarchy) and it is carried by weight and colour together: the rail, the headline
// weight and the kind tag. Three levels: critical = red, warn = amber, watch = gray.
// Only critical is red.
//
// The shared tint vocabulary is warm/cool/positive, with no amber, so the map is local:
// critical borrows the shared `warm` classes where they fit; warn and watch are local.
const WARM = tintClasses("warm");
// The headline's layout, shared by the title and the server sentence. Below lg it is a
// full-width block that wraps (an unbroken token breaks rather than overflowing); at lg+ it
// is the row's flexible middle, one line long and cut with an ellipsis by CSS only — the
// text itself is never shortened, and the hover title carries all of it. 15px is the
// Clients list's headline size.
const HEADLINE_LAYOUT =
  "order-3 block w-full break-words text-[15px] leading-snug lg:order-none lg:w-auto lg:min-w-0 lg:flex-1 lg:truncate";

/**
 * Per-severity classes for a row's rail, headline and kind tag.
 */
export const SEVERITY_STYLE = {
  critical: {
    rail: WARM.dot,
    headline: "font-normal text-gray-900 dark:text-gray-100",
    tag: `bg-red-50 dark:bg-red-900/30 ${WARM.text}`,
  },
  warn: {
    rail: "bg-amber-500",
    headline: "font-normal text-gray-800 dark:text-gray-200",
    tag: "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  },
  watch: {
    rail: "bg-gray-300 dark:bg-gray-600",
    headline: "font-normal text-gray-700 dark:text-gray-300",
    tag: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
  },
};

/**
 * The blue behind a row that arrived through the "N new" pill, in the pill's own blue family.
 * Chosen to stand clearly apart from the list's white / gray-900 surface (CIE76 ΔE about 14 in
 * light and 19 in dark, against 6 and 10 for the lighter blue-50 / blue-900/20 it replaced) and
 * from the gray hover shade.
 */
export const NEW_ROW_TINT = "bg-blue-100 dark:bg-blue-900/40";

// Gray-500 text sits at 3.96:1 on the light new-row tint, under AA, so a tinted row steps its
// secondary text one shade darker (6.2:1). Dark text clears AA as is.
const TINT_TEXT_STEP = [["text-gray-500", "text-gray-600"]];

// The client's slot; the link, the plain name and the placeholder for a row with no client all
// share it, so the row has the same boxes whichever it is. Below lg it shares a line with the
// actions; at lg+ it takes its own width up to a cap.
const CLIENT_SLOT = "order-5 min-w-0 flex-1 lg:order-none lg:max-w-48 lg:flex-none";

// A client name that is not a link (no client id to open) and the placeholder: muted text.
const CLIENT_LINE = `${CLIENT_SLOT} truncate text-sm text-gray-500 dark:text-gray-400`;

// The client link: a blue link (the kit's #32B1FF is under AA on white) that underlines on
// hover and cuts a long name.
const CLIENT_LINK =
  "block max-w-full truncate text-left text-sm capitalize text-blue-700 hover:underline dark:text-sky-400 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500";

/** What the client slot says when an item has no client name (the server leaves it null on many rows). */
export const NO_CLIENT_TEXT = "No client linked";

/** The kind tag's shape and type; the colours come from SEVERITY_STYLE. */
export const TAG_CLASS = "min-w-0 truncate rounded-md px-2 py-0.5 text-xs font-semibold uppercase tracking-wider";

/**
 * Adapter: maps one `items[]` element onto the row surface + its affordances. Thin —
 * it composes props, it does not reimplement the card shell or the sentence.
 *
 * At lg+ a row is ONE line: the severity rail, the kind tag, the headline (the flexible
 * middle, cut with an ellipsis by CSS when it does not fit), the client, the owner in
 * Everyone scope, then the action buttons. Below lg the same pieces stack: the tag, the
 * headline, the muted detail line, then client and actions.
 *
 * The headline is always the server's `says`, verbatim (through <Sentence>), for every
 * kind. The item's `title` is the detail: a muted line below lg, a screen-reader-only copy
 * at every size, and the second line of the headline's hover title. The hover title also
 * carries the whole `says`, so a sentence the row has cut is still readable. The sentence
 * is clipped by CSS only; nothing rewrites it.
 *
 * Severity drives the rail, headline shade and tag. A non-dismissable row renders NO
 * dismiss control (not a disabled one). An unknown kind renders with only the row dismiss
 * control and a humanised tag.
 *
 * The client is a link that asks the page to open that client's slide-out (`onOpenClient`),
 * which the page renders once, outside the list. Without a client id, or without a handler,
 * there is nothing to open, so a named client is plain text; an item with no client name
 * says so in a muted italic.
 *
 * The owner is shown whenever one is set, in either scope, after the client. An
 * unowned task or thread row offers a dashed Assign, and an unowned needs-help task a
 * primary Take.
 *
 * @param {object} props
 * @param {object} props.item                One fn_now item.
 * @param {Record<string, Function>} [props.handlers]  action-name → handler(item[, text]).
 * @param {(item: object) => void} [props.onDismiss]   Row dismiss (optimistic, at NowPage).
 * @param {boolean} [props.isNew]            The row arrived through the "N new" pill and is still tinted.
 * @param {(item: object, how: "pointer"|"click"|"focus") => void} [props.onSeen]  Called when the reader points at, clicks or focuses a new row.
 * @param {(item: object) => void} [props.onOpen]  Opens a promise's detail panel. Only a
 *   `commitment` row uses it: the row is clickable and its headline becomes a button.
 * @param {(client: {clientId: string, clientName: string}) => void} [props.onOpenClient]
 *   Opens the client's slide-out. Without it the client is plain text.
 */
export default function NowRow({
  item,
  handlers = {},
  onDismiss,
  isNew = false,
  onSeen,
  onOpen,
  onOpenClient,
}) {
  const {
    id,
    kind,
    rank,
    severity,
    client,
    client_id: clientId,
    owner,
    dismissable,
    dismiss_label: dismissLabel,
    subject_type: subjectType,
  } = item;
  const [composerOpen, setComposerOpen] = useState(false);
  const detailId = useId();
  // Affordances route on the row's subject_type (with kind-specific overrides), so a
  // new server-side kind gets the right control by its subject_type alone.
  // A link affordance (`to`) with no target for this row is not rendered at all.
  const affordances = affordancesFor(item).filter((a) => !a.to || a.to(item));
  const showAssign = canAssign(item);
  const style = SEVERITY_STYLE[severity] || SEVERITY_STYLE.watch;
  const headline = rowHeadline(item);
  const label = kindLabel(kind);
  // A blank or whitespace-only name is as good as none.
  const hasClient = typeof client === "string" && client.trim() !== "";
  const hasDetail = !!headline.detail;
  const linksClient = hasClient && !!clientId && !!onOpenClient;
  // Two threads from one client can carry the same sentence ("X has been waiting 4 days for a
  // real reply"), so the sentence alone does not tell their rows apart. At xl+ the item's title,
  // which is the thread's subject, therefore sits on the line as muted text after the sentence.
  // A promise keeps it off the line (its sentence already names the conversation) and so does a
  // title the sentence already contains.
  const titleOnLine =
    hasDetail &&
    headline.source === "says" &&
    subjectType !== "commitment" &&
    !headline.text.toLowerCase().includes(headline.detail.toLowerCase());
  // Hover text for the headline: the whole sentence, since lg+ may have cut it, then the
  // detail on a line of its own, since lg+ does not show it. A promise's headline button is
  // described by the detail's screen-reader copy instead (aria-describedby wins over `title`),
  // so assistive tech hears the detail once.
  const headlineTitle = hasDetail ? `${headline.text}\n${headline.detail}` : headline.text || undefined;

  // The row's secondary text, darkened one shade while the new-row tint is behind it.
  const onTint = (classes) =>
    isNew ? TINT_TEXT_STEP.reduce((c, [from, to]) => c.replace(from, to), classes) : classes;

  // Pointing at, clicking or focusing a new row is the reader seeing it: the tint clears.
  const markSeen = (how) => {
    if (isNew) onSeen?.(item, how);
  };

  // Only a promise opens a detail panel; every other kind's row stays inert.
  const opensPanel = !!onOpen && subjectType === "commitment";
  // A click on a control inside the row (the client link included) belongs to that control,
  // not to the row.
  const onRowClick = (e) => {
    markSeen("click");
    if (!opensPanel) return;
    if (e.target.closest("button, a, input, textarea, select, [role='menu']")) return;
    onOpen(item);
  };

  const runAction = (action) => {
    if (action === "composer") {
      setComposerOpen(true);
      return;
    }
    handlers[action]?.(item);
  };

  // A dismiss that only repeats an affordance's meaning is not shown (an agent row whose
  // server dismiss label is "Back to draft" already has Edit, which returns it to draft).
  const dismissText = (dismissLabel || "Dismiss").trim().toLowerCase();
  const repeatsAffordance = affordances.some(
    (a) => a.title && a.title.trim().toLowerCase() === dismissText,
  );
  // needs_assistance is suppressed too: fn_now_dismiss refuses it server-side ("move the
  // work instead"), so its Move work menu is the only exit.
  const showDismiss = dismissable && kind !== "needs_assistance" && !repeatsAffordance;
  const hasActions = affordances.length > 0 || showDismiss || showAssign;
  const actionClass = (variant) => (variant === "primary" ? BTN_PRIMARY : BTN_QUIET);

  return (
    <div
      data-now-row={id}
      data-rank={rank}
      data-kind={kind}
      data-now-new={isNew ? "true" : undefined}
      tabIndex={-1}
      onMouseEnter={() => markSeen("pointer")}
      onClick={onRowClick}
      onFocus={() => markSeen("focus")}
      className={`${opensPanel ? "cursor-pointer " : ""}relative flex flex-wrap items-center gap-x-3 gap-y-1.5 py-3 pl-4 pr-3 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-400 dark:hover:bg-gray-800/60 lg:min-h-[44px] lg:gap-x-4 lg:py-1.5 ${
        isNew ? NEW_ROW_TINT : ""
      }`}
    >
      {isNew && <span className="sr-only">New. </span>}
      <span
        data-now-rail
        aria-hidden="true"
        className={`absolute inset-y-0 left-0 w-1 ${style.rail}`}
      />

      {/* The row's pieces are direct children of one wrapping flex row. Below lg their `order`
          classes stack them: tag, headline, detail, client and owner, actions. At lg+ they run
          in DOM order on one line, the headline taking the width the others leave. */}
      <span className="order-1 flex min-w-0 lg:order-none lg:max-w-44 lg:shrink-0">
        <span
          data-now-tag
          title={label}
          className={`${TAG_CLASS} ${style.tag}`}
        >
          {label}
        </span>
      </span>

      {/* The server's sentence leads, rendered verbatim and cut by CSS only. A promise's
          headline is a button too — the keyboard route into its panel — laid out as the span
          is. The title stands in only when the item has no sentence, and then it is the
          subject. */}
      {opensPanel ? (
        <button
          type="button"
          data-now-headline
          data-now-subject={headline.source === "title" ? "" : undefined}
          data-now-open
          title={headlineTitle}
          aria-describedby={hasDetail ? detailId : undefined}
          onClick={() => onOpen(item)}
          className={`${HEADLINE_LAYOUT} ${style.headline} text-left`}
        >
          {headline.text}
        </button>
      ) : headline.source === "title" ? (
        <span
          data-now-headline
          data-now-subject
          title={headlineTitle}
          className={`${HEADLINE_LAYOUT} ${style.headline}`}
        >
          {headline.text}
        </span>
      ) : (
        <Sentence
          says={headline.text}
          size="none"
          as="span"
          data-now-headline
          title={headlineTitle}
          className={`${HEADLINE_LAYOUT} ${style.headline}`}
        />
      )}

      {/* The item's title: a muted line below lg, muted text on the line at xl+ when it tells
          rows apart (see titleOnLine), and a screen-reader-only copy at every size. The visible
          copies are hidden from assistive tech, which gets the copy below. */}
      {hasDetail && (
        <>
          <span
            data-now-detail
            data-now-subject
            aria-hidden="true"
            title={titleOnLine ? headline.detail : undefined}
            className={onTint(
              `order-4 block w-full break-words text-sm text-gray-500 dark:text-gray-400 ${
                titleOnLine
                  ? "lg:hidden xl:order-none xl:block xl:w-auto xl:min-w-0 xl:max-w-[30%] xl:shrink xl:truncate"
                  : "lg:hidden"
              }`,
            )}
          >
            {headline.detail}
          </span>
          {/* A promise's button takes this as its description, so here it is not read again in
              the flow. */}
          <span
            id={opensPanel ? detailId : undefined}
            aria-hidden={opensPanel ? "true" : undefined}
            data-now-detail-sr
            data-now-subject
            className="sr-only"
          >
            {headline.detail}
          </span>
        </>
      )}

      {/* The client, then the owner whenever one is set, in either scope, so a row without an
          Assign button visibly has an owner. The client opens its slide-out; with no
          client id to open it is plain text, and with no name the slot says so in a muted
          italic. */}
      {linksClient ? (
        <div data-now-client className={`${CLIENT_SLOT} flex`}>
          <button
            type="button"
            data-testid="client-slideout-trigger"
            title={client}
            onClick={() => onOpenClient({ clientId, clientName: client })}
            className={CLIENT_LINK}
          >
            {client}
          </button>
        </div>
      ) : hasClient ? (
        <span data-now-client title={client} className={onTint(`${CLIENT_LINE} capitalize`)}>
          {client}
        </span>
      ) : (
        <span data-now-client-placeholder className={onTint(`${CLIENT_LINE} italic`)}>
          {NO_CLIENT_TEXT}
        </span>
      )}
      {owner && (
        <span
          data-owner={owner}
          title={`Owner: ${owner}`}
          className={onTint("order-6 max-w-32 shrink-0 truncate text-sm text-gray-500 dark:text-gray-400 lg:order-none lg:max-w-48 lg:before:mr-3 lg:before:content-['·']")}
        >
          {owner}
        </span>
      )}

      {hasActions && (
        <div className="order-7 ml-auto flex flex-wrap items-center gap-2 lg:order-none lg:ml-0 lg:shrink-0 lg:flex-nowrap lg:justify-end">
          {affordances.map((a) =>
            a.action === "take" ? (
              // Take assigns the task to the signed-in user (same write as Assign).
              <NowTakeButton
                key={a.key}
                item={item}
                onAssigned={handlers.onAssigned}
                onError={handlers.onAssignError}
              />
            ) : a.action === "moveWork" ? (
              // needs_assistance → the status-choice menu (fn_now_dismiss refuses
              // it server-side): moving to a non-blocked status is the row's real
              // exit, so the row offers the choice rather than a one-click.
              <MoveWorkMenu
                key={a.key}
                workType={item.work_type}
                taskId={id}
                buttonLabel={a.label}
                variant={a.variant}
                buttonClassName={actionClass(a.variant)}
                popoverClassName="right-0"
                onMoved={() => handlers.onWorkMoved?.(item)}
              />
            ) : a.to ? (
              <Link
                key={a.key}
                to={a.to(item)}
                data-action={a.action}
                className={actionClass(a.variant)}
              >
                {a.label}
              </Link>
            ) : (
              <button
                key={a.key}
                type="button"
                data-action={a.action}
                title={a.title}
                aria-label={a.ariaLabel}
                onClick={() => runAction(a.action)}
                className={actionClass(a.variant)}
              >
                {a.label}
              </button>
            ),
          )}

          {/* dismissable:false → render NO control at all (not a disabled one). */}
          {showDismiss && (
            <button
              type="button"
              data-dismiss={id}
              onClick={() => onDismiss?.(item)}
              className={BTN_QUIET}
            >
              {dismissLabel || "Dismiss"}
            </button>
          )}

          {/* Only an unowned task or thread row can be assigned — those are the
              subjects with an assign write. Anything else shows no Assign at all. */}
          {showAssign && (
            <NowAssignControl
              item={item}
              onAssigned={handlers.onAssigned}
              onError={handlers.onAssignError}
            />
          )}
        </div>
      )}

      {/* Reply composer — kit/Composer has no `open` prop; it sends via composeSend.
          threadId/taskId route by the row's subject_type. */}
      {composerOpen && (
        <div className="order-last w-full basis-full pb-1">
          <Composer
            threadId={subjectType === "thread" ? id : null}
            taskId={subjectType === "task" ? id : null}
            clientId={clientId}
            onSent={() => setComposerOpen(false)}
            onCancel={() => setComposerOpen(false)}
          />
        </div>
      )}
    </div>
  );
}
